import { type ReactNode, useEffect, useRef } from 'react';

import { useFetcher, useOutletContext, useParams, useSearchParams } from '@remix-run/react';

import useToast from '~/hooks/useToast';
import { ACTIVE_UPGRADE_COOKIE_NAME } from '~/hooks/useUtils/useCookies';
import type { OutletContext } from '~/interfaces/outlet_context';

enum ToastStatus {
  SUCCESS = 'success',
  ERROR = 'error',
}

type Props = {
  onSuccess?: () => void;
  successMessage?: string;
  children?: ({ isLoading, submitForm }: { isLoading: boolean; submitForm: () => void }) => ReactNode;
};

const ReportAbuseAction = ({ children, onSuccess, successMessage }: Props) => {
  const [searchParams] = useSearchParams();
  const fetcher = useFetcher();
  const params = useParams();
  const { subscriberId } = params;
  const fetcherData = fetcher.data as {
    toast: { status: ToastStatus; message: string };
  };
  const isSubmitting = fetcher.state === 'submitting';
  const formRef = useRef<HTMLFormElement>(null);
  const { removeCookie, cookies } = useOutletContext<OutletContext>();
  const status = fetcherData?.toast?.status;

  useToast(fetcherData?.toast);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We don't want this to run on every render
  useEffect(() => {
    if (status === 'success') {
      removeCookie(ACTIVE_UPGRADE_COOKIE_NAME);
      onSuccess?.();
    }
  }, [status]);

  const submitForm = () => {
    if (formRef.current) {
      fetcher.submit(formRef.current);
    }
  };

  return (
    <fetcher.Form ref={formRef} method="post" action="/report_abuse">
      <input hidden name="subscriber_id" defaultValue={subscriberId} />
      {searchParams.has('post_id') && <input hidden name="post_id" defaultValue={searchParams.get('post_id') || ''} />}
      <input hidden name="last_resource_guid" defaultValue={cookies.last_resource_guid ?? ''} />
      <input
        hidden
        name="report_abuse_success_message"
        defaultValue={successMessage || 'You have been unsubscribed and the publication has been reported.'}
      />
      {children?.({ isLoading: isSubmitting, submitForm })}
    </fetcher.Form>
  );
};

export default ReportAbuseAction;
